import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CircularProgress,
  Fab,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AccountBalance,
  Refresh,
  Logout,
  Add,
  Edit,
  Delete,
  CloudUpload,
  Calculate,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatPercentage } from '../../utils/navCalculations';
import { formatMultiLineChartData } from '../../utils/chartHelpers';
import MultiLinePerformanceChart from '../../components/charts/MultiLinePerformanceChart';
import PerformanceMetricsCard from '../../components/admin/PerformanceMetricsCard';

// Import real hooks
import { useLenders } from '../../hooks/useLenders';
import { useSeries } from '../../hooks/useSeries';
import { useTotalAUM } from '../../hooks/useSeries';
import { usePoolNAVHistory } from '../../hooks/useNAVHistory';
import { usePendingRedemptions } from '../../hooks/useRedemptions';
import { useFundFees } from '../../hooks/useFundFees';

// Import dialogs
import AddLenderDialog from '../../components/dialogs/AddLenderDialog';
import CreateInvestorDialog from '../../components/dialogs/CreateInvestorDialog';
import CreateSeriesDialog from '../../components/dialogs/CreateSeriesDialog';
import EditLenderDialog from '../../components/dialogs/EditLenderDialog';
import UploadNAVDialog from '../../components/dialogs/UploadNAVDialog';
import CrystallisationDialog from '../../components/dialogs/CrystallisationDialog';
import { Lender } from '../../types';
import { getLastNAVUpdate, PoolNAVUpdate } from '../../services/navUpdateService';

// Import admin components
import RedemptionsQueue from '../../components/admin/RedemptionsQueue';
import SeriesListTable from '../../components/admin/SeriesListTable';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Real Firestore data (with real-time updates!)
  const { lenders, loading: loadingLenders, removeLender } = useLenders(true);
  const { series, loading: loadingSeries } = useSeries(true);
  const { totalAUM, loading: loadingAUM, refresh: refreshAUM } = useTotalAUM();
  const { history: navHistory } = usePoolNAVHistory(30, true);
  const { redemptions: pendingRedemptions, loading: loadingRedemptions } = usePendingRedemptions(true);
  const { fundFees, loading: loadingFundFees } = useFundFees(true);

  // Dialog states
  const [addLenderOpen, setAddLenderOpen] = useState(false);
  const [createInvestorOpen, setCreateInvestorOpen] = useState(false);
  const [createSeriesOpen, setCreateSeriesOpen] = useState(false);
  const [editLenderOpen, setEditLenderOpen] = useState(false);
  const [uploadNAVOpen, setUploadNAVOpen] = useState(false);
  const [crystalliseOpen, setCrystalliseOpen] = useState(false);
  const [selectedLender, setSelectedLender] = useState<Lender | null>(null);
  const [lastNAVUpdate, setLastNAVUpdate] = useState<PoolNAVUpdate | null>(null);

  // Load last NAV update on mount
  React.useEffect(() => {
    const loadLastUpdate = async () => {
      const update = await getLastNAVUpdate();
      setLastNAVUpdate(update);
    };
    loadLastUpdate();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleEditLender = (lender: Lender) => {
    setSelectedLender(lender);
    setEditLenderOpen(true);
  };

  const handleDeleteLender = async (lenderId: string, lenderName: string) => {
    if (window.confirm(`Are you sure you want to delete ${lenderName}?\n\nThis action cannot be undone.`)) {
      try {
        await removeLender(lenderId);
      } catch (error) {
        alert('Failed to delete lender');
      }
    }
  };

  const handleRefresh = () => {
    refreshAUM();
  };

  // Calculate metrics - ONLY for ACTIVE series (exclude closed/redeemed)
  const activeSeries = series.filter(s => s.status === 'active');
  const totalPrincipal = activeSeries.reduce((sum, s) => sum + s.principalAmount, 0);
  const totalNAV = activeSeries.reduce((sum, s) => sum + s.currentNAV, 0); // From IB trading ONLY
  const totalCapitalizedInterest = activeSeries.reduce((sum, s) => sum + (s.capitalizedInterest || 0), 0); // Tracked but NOT added to NAV
  const totalReturn = totalPrincipal > 0 ? ((totalNAV - totalPrincipal) / totalPrincipal) * 100 : 0;
  const totalGains = totalNAV - totalPrincipal; // Trading gains only

  // Prepare multi-line chart data
  // FIX Issue #011: Ensure data is in chronological order (oldest first)
  const multiLineChartData = formatMultiLineChartData(navHistory, totalPrincipal).reverse();

  const loading = loadingLenders || loadingSeries || loadingAUM;

  const MetricCard = ({ title, value, icon: Icon, color }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color }}>
              {value}
            </Typography>
          </Box>
          <Icon sx={{ fontSize: 48, color, opacity: 0.3 }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Top AppBar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main' }}>
            Maverick Capital - Admin Dashboard
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateInvestorOpen(true)}
            sx={{
              bgcolor: '#4CAF50',
              color: '#FFF',
              '&:hover': { bgcolor: '#45A049' },
              mr: 2,
            }}
          >
            Create Investor
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setUploadNAVOpen(true)}
            sx={{
              bgcolor: '#FFD700',
              color: '#000',
              '&:hover': { bgcolor: '#FFC700' },
              mr: 2,
            }}
          >
            Upload NAV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Calculate />}
            onClick={() => setCrystalliseOpen(true)}
            sx={{
              borderColor: '#FFD700',
              color: '#FFD700',
              '&:hover': { borderColor: '#FFC700', bgcolor: 'rgba(255, 215, 0, 0.1)' },
              mr: 2,
            }}
          >
            Crystallise
          </Button>
          <IconButton color="inherit" onClick={handleRefresh} title="Refresh Data">
            <Refresh />
          </IconButton>
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ ml: 2 }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress sx={{ color: '#FFD700' }} size={60} />
          </Box>
        ) : (
          <>
            {/* Key Metrics */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 3, mb: 4 }}>
              <MetricCard
                title="Total Principal"
                value={formatCurrency(totalPrincipal)}
                icon={AccountBalance}
                color="#FFFFFF"
              />
              <MetricCard
                title="Total NAV"
                value={formatCurrency(totalNAV)}
                icon={TrendingUp}
                color="#FFD700"
              />
              <MetricCard
                title="Fund Fees"
                value={formatCurrency(fundFees)}
                icon={TrendingUp}
                color="#4CAF50"
              />
              <MetricCard
                title="Total Gains"
                value={formatCurrency(totalGains)}
                icon={TrendingUp}
                color={totalGains >= 0 ? '#4CAF50' : '#F44336'}
              />
              <MetricCard
                title="Total Return"
                value={formatPercentage(totalReturn / 100)}
                icon={TrendingUp}
                color={totalReturn >= 0 ? '#4CAF50' : '#F44336'}
              />
              <MetricCard
                title="Lenders / Series"
                value={`${lenders.length} / ${series.length}`}
                icon={People}
                color="#FFFFFF"
              />
            </Box>

            {/* Enhanced Multi-Line Performance Chart (Sprint 7!) */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#FFD700' }}>
                    📈 Enhanced Performance Chart (Last 30 Days)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                    ✨ Sprint 7: Multi-line with toggles!
                  </Typography>
                </Box>
                
                {multiLineChartData.length > 0 ? (
                  <MultiLinePerformanceChart 
                    data={multiLineChartData}
                    height={350}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="h6" sx={{ color: '#999', mb: 2 }}>
                      📊 No NAV History Available
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                      Upload NAV data using the "Upload NAV" button above to see performance charts.
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                      💡 Tip: The chart will show NAV, Principal, and Gains once data is uploaded.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Performance Metrics Dashboard (Sprint 7 Phase 2) */}
            <Box sx={{ mb: 4 }}>
              <PerformanceMetricsCard series={series} hurdleRate={3} />
            </Box>

            {/* Series List Table - FIX Issue #009 & #010 */}
            <Box sx={{ mb: 4 }}>
              <SeriesListTable series={series} lenders={lenders} />
            </Box>

            {/* Redemptions Queue */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#FFD700', mb: 2 }}>
                Redemption Management
              </Typography>
              <RedemptionsQueue />
            </Box>

            {/* Lenders Table */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Lenders ({lenders.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddLenderOpen(true)}
                    sx={{
                      bgcolor: '#FFD700',
                      color: '#000',
                      '&:hover': { bgcolor: '#FFC700' },
                    }}
                  >
                    Add Lender
                  </Button>
                </Box>

                {lenders.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                    No lenders yet. Click "Add Lender" to create your first lender.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #333' }}>
                          <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Name</th>
                          <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Email</th>
                          <th style={{ textAlign: 'left', padding: '12px', color: '#FFD700' }}>Phone</th>
                          <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Series</th>
                          <th style={{ textAlign: 'center', padding: '12px', color: '#FFD700' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lenders.map((lender) => (
                          <tr key={lender.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '12px' }}>{lender.fullName}</td>
                            <td style={{ padding: '12px' }}>{lender.email}</td>
                            <td style={{ padding: '12px' }}>{lender.phone}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {series.filter(s => s.lenderId === lender.id).length}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditLender(lender)}
                                sx={{ color: '#FFD700', mr: 1 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteLender(lender.id, lender.fullName)}
                                sx={{ color: '#ff4444' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Status Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                ✓ Connected to Firebase • Real-time updates enabled
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Sprint 2: Lender & Series CRUD - Fully Operational
              </Typography>
            </Box>
          </>
        )}
      </Container>

      {/* Floating Action Button for Create Series */}
      <Fab
        color="primary"
        aria-label="create series"
        onClick={() => setCreateSeriesOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          bgcolor: '#FFD700',
          color: '#000',
          '&:hover': { bgcolor: '#FFC700' },
        }}
      >
        <Add />
      </Fab>

      {/* Dialogs */}
      <CreateInvestorDialog
        open={createInvestorOpen}
        onClose={() => setCreateInvestorOpen(false)}
        onSuccess={() => {
          setCreateInvestorOpen(false);
          refreshAUM();
        }}
      />

      <AddLenderDialog
        open={addLenderOpen}
        onClose={() => setAddLenderOpen(false)}
        onSuccess={() => {
          setAddLenderOpen(false);
          refreshAUM();
        }}
      />

      <CreateSeriesDialog
        open={createSeriesOpen}
        onClose={() => setCreateSeriesOpen(false)}
        onSuccess={() => {
          setCreateSeriesOpen(false);
          refreshAUM();
        }}
      />

      <EditLenderDialog
        open={editLenderOpen}
        lender={selectedLender}
        onClose={() => {
          setEditLenderOpen(false);
          setSelectedLender(null);
        }}
        onSuccess={() => {
          setEditLenderOpen(false);
          setSelectedLender(null);
        }}
      />

      <UploadNAVDialog
        open={uploadNAVOpen}
        onClose={() => setUploadNAVOpen(false)}
        onSuccess={async () => {
          setUploadNAVOpen(false);
          refreshAUM();
          // Reload last NAV update
          const update = await getLastNAVUpdate();
          setLastNAVUpdate(update);
        }}
      />

      <CrystallisationDialog
        open={crystalliseOpen}
        series={series.filter(s => s.status === 'active')}
        onClose={() => setCrystalliseOpen(false)}
        onSuccess={() => {
          setCrystalliseOpen(false);
          refreshAUM();
        }}
      />
    </Box>
  );
};

export default AdminDashboard;
